import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { MdOutlineEdit, MdDeleteForever } from "react-icons/md";
import { IoRefreshCircleOutline } from "react-icons/io5";

import { MdDashboard } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Dashboard.css";

import banner3 from "../banner3.png";
import {
  getAllHotelCertificationDetails,
  deleteHotels,
} from "../../utils/apiService";

const Dashboard = () => {
  const [hotelData, setHotelData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [hotelToDelete, setHotelToDelete] = useState(null);
  const [selectedHotels, setSelectedHotels] = useState([]);
  const rowsPerPage = 6;
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchHotelData();
  }, []);

  const fetchHotelData = async () => {
    setIsRefreshing(true);
    try {
      const data = await getAllHotelCertificationDetails();
      console.log("Fetched Hotel Data:", data);
      setHotelData(data);
      toast.success("Data refreshed successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to refresh data. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    if (!sortConfig.key) return hotelData;

    return [...hotelData].sort((a, b) => {
      // Get values and convert to lowercase if they're strings
      let valueA = a[sortConfig.key];
      let valueB = b[sortConfig.key];

      // Handle boolean values (for isCurrentlyCertified)
      if (typeof valueA === "boolean") {
        valueA = valueA ? 1 : 0;
        valueB = valueB ? 1 : 0;
      }
      // Handle numeric values (for totalPoints)
      else if (typeof valueA === "number") {
        return sortConfig.direction === "ascending"
          ? valueA - valueB
          : valueB - valueA;
      }
      // Handle string values
      else {
        valueA = String(valueA).toLowerCase();
        valueB = String(valueB).toLowerCase();
      }

      // Common comparison logic
      if (valueA < valueB) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <span className="sort-icon">↕</span>;
    }
    return (
      <span className="sort-icon active">
        {sortConfig.direction === "ascending" ? "↑" : "↓"}
      </span>
    );
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = getSortedData().slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(hotelData.length / rowsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleModify = (hotelId) => {
    navigate(`/details/${hotelId}`);
  };

  const handleSelectHotel = (hotelId) => {
    setSelectedHotels((prev) => {
      if (prev.includes(hotelId)) {
        return prev.filter((id) => id !== hotelId);
      } else {
        return [...prev, hotelId];
      }
    });
  };

  const handleBulkDeleteClick = () => {
    if (selectedHotels.length === 0) {
      toast.warning("Please select projects to delete");
      return;
    }
    setHotelToDelete(selectedHotels);
    setShowDeletePopup(true);
  };

  const handleDelete = async () => {
    try {
      const idsToDelete = Array.isArray(hotelToDelete)
        ? hotelToDelete
        : [hotelToDelete];

      await deleteHotels(idsToDelete);

      // Update the UI by removing the deleted hotels
      setHotelData(
        hotelData.filter((hotel) => !idsToDelete.includes(hotel.hotelId))
      );
      setSelectedHotels([]); // Clear selections after delete

      toast.success(`${idsToDelete.length} project(s) deleted successfully`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error("Error deleting hotel(s):", error);
      toast.error("Failed to delete hotel(s). Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setShowDeletePopup(false);
      setHotelToDelete(null);
    }
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setHotelToDelete(null);
  };

  return (
    <div className="dashboard-wrapper">
      <img src={banner3} alt="Raddison Logo" className="company-logo-d" />
      <div className="dashboard-header">
        <div className="header-title">
          <h1>
            <MdDashboard />
            Dashboard
          </h1>
        </div>

        <div className="header-actions">
          <button
            className="icon-button"
            title="Delete Selected"
            onClick={handleBulkDeleteClick}
          >
            <MdDeleteForever />
          </button>
          <button
            className={`icon-button ${isRefreshing ? "refreshing" : ""}`}
            title="Refresh"
            onClick={fetchHotelData}
            disabled={isRefreshing}
          >
            <IoRefreshCircleOutline />
          </button>
          <a
            className="new-project-btn"
            href="/details"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            New Project +
          </a>
        </div>
      </div>

      <div className="table-container">
        <table className="certification-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedHotels.length === currentRows.length}
                  onChange={() => {
                    if (selectedHotels.length === currentRows.length) {
                      setSelectedHotels([]);
                    } else {
                      setSelectedHotels(
                        currentRows.map((hotel) => hotel.hotelId)
                      );
                    }
                  }}
                />
              </th>
              <th onClick={() => handleSort("hotelName")}>
                Hotel Name {getSortIcon("hotelName")}
              </th>
              <th onClick={() => handleSort("location")}>
                Location {getSortIcon("location")}
              </th>
              <th onClick={() => handleSort("calculatedCertificationLevel")}>
                Eligible for Certification{" "}
                {getSortIcon("calculatedCertificationLevel")}
              </th>
              <th onClick={() => handleSort("totalPoints")}>
                Points <br></br> Achieved {getSortIcon("totalPoints")}
              </th>
              <th onClick={() => handleSort("isCurrentlyCertified")}>
                Already Green <br></br> Certified?{" "}
                {getSortIcon("isCurrentlyCertified")}
              </th>
              <th onClick={() => handleSort("managerName")}>
                Manager {getSortIcon("managerName")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((hotel, index) => (
              <tr key={index}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedHotels.includes(hotel.hotelId)}
                    onChange={() => handleSelectHotel(hotel.hotelId)}
                  />
                </td>
                <td>
                  <div className="hotel-name">{hotel.hotelName}</div>
                </td>
                <td>{hotel.city}</td>
                <td data-certification={hotel.calculatedCertificationLevel}>
                  {hotel.calculatedCertificationLevel}
                </td>
                <td>{hotel.totalPoints}</td>
                <td data-status={hotel.isCurrentlyCertified ? "Yes" : "No"}>
                  {hotel.isCurrentlyCertified ? "Yes" : "No"}
                </td>
                <td>{hotel.managerName}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="icon-button modify-btn"
                      title="Modify"
                      onClick={() => handleModify(hotel.hotelId)}
                    >
                      <MdOutlineEdit />
                      <span>Edit</span>
                    </button>
                    <button
                      className="icon-button delete-btn"
                      title="Delete"
                      onClick={() => {
                        setHotelToDelete(hotel.hotelId);
                        setShowDeletePopup(true);
                      }}
                    >
                      <MdDeleteForever />
                      <span>Delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <div className="pagination-numbers">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`pagination-btn ${
                  currentPage === i + 1 ? "active" : ""
                }`}
                onClick={() => paginate(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            className="pagination-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Delete Confirmation Popup */}
      {showDeletePopup && (
        <div className="popup-overlay">
          <div className="popup-container">
            <h3>Confirm Deletion</h3>
            <p>
              {Array.isArray(hotelToDelete)
                ? `Are you sure you want to delete ${hotelToDelete.length} selected project(s)?`
                : "Are you sure you want to delete this Project?"}
            </p>
            <div className="popup-buttons">
              <button className="btn-delete" onClick={handleDelete}>
                Yes
              </button>
              <button className="btn-cancel" onClick={closeDeletePopup}>
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
};

export default Dashboard;
